import { supabase } from '../lib/supabase';

export const whiteLabelService = {
  // Get current branding settings
  async getSettings() {
    try {
      // Remove RLS restriction - allow all users to read branding settings
      const { data, error } = await supabase
        ?.from('white_label_settings')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        console.error('White label settings error:', error);
        // Don't throw error, return null to use fallback
        return null;
      }

      // If no settings exist, return null (will use defaults in Header)
      if (!data) {
        return null;
      }

      // Get public URLs for logo and favicon
      let logoUrl = null;
      let faviconUrl = null;

      if (data?.logo_path) {
        const { data: logoData } = supabase?.storage?.from('branding-assets')?.getPublicUrl(data?.logo_path);
        logoUrl = logoData?.publicUrl;
      }

      if (data?.favicon_path) {
        const { data: faviconData } = supabase?.storage?.from('branding-assets')?.getPublicUrl(data?.favicon_path);
        faviconUrl = faviconData?.publicUrl;
      }

      return {
        id: data?.id,
        brandName: data?.brand_name,
        slogan: data?.slogan,
        logoPath: data?.logo_path,
        faviconPath: data?.favicon_path,
        logoUrl,
        faviconUrl,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching white label settings:', error);
      // Return null instead of throwing to allow fallback to defaults
      return null;
    }
  },

  // Update branding settings
  async updateSettings(settings) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // First, check if any settings exist
      const { data: existingSettings } = await supabase
        ?.from('white_label_settings')
        ?.select('id')
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();

      let data, error;

      if (existingSettings?.id) {
        // Update existing settings
        const updateResult = await supabase
          ?.from('white_label_settings')
          ?.update({
            brand_name: settings?.brandName,
            slogan: settings?.slogan,
            logo_path: settings?.logoPath,
            favicon_path: settings?.faviconPath
          })
          ?.eq('id', existingSettings?.id)
          ?.select()
          ?.single();
        
        data = updateResult?.data;
        error = updateResult?.error;
      } else {
        // Insert new settings
        const insertResult = await supabase
          ?.from('white_label_settings')
          ?.insert({
            brand_name: settings?.brandName,
            slogan: settings?.slogan,
            logo_path: settings?.logoPath,
            favicon_path: settings?.faviconPath,
            created_by: user?.id
          })
          ?.select()
          ?.single();
        
        data = insertResult?.data;
        error = insertResult?.error;
      }

      if (error) throw error;

      return {
        id: data?.id,
        brandName: data?.brand_name,
        slogan: data?.slogan,
        logoPath: data?.logo_path,
        faviconPath: data?.favicon_path
      };
    } catch (error) {
      console.error('Error updating white label settings:', error);
      throw error;
    }
  },

  // Upload logo
  async uploadLogo(file) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase?.storage?.from('branding-assets')?.upload(filePath, file);

      if (uploadError) throw uploadError;

      return filePath;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  },

  // Upload favicon
  async uploadFavicon(file) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `favicon-${Date.now()}.${fileExt}`;
      const filePath = `favicons/${fileName}`;

      const { error: uploadError } = await supabase?.storage?.from('branding-assets')?.upload(filePath, file);

      if (uploadError) throw uploadError;

      return filePath;
    } catch (error) {
      console.error('Error uploading favicon:', error);
      throw error;
    }
  },

  // Delete file from storage
  async deleteFile(filePath) {
    try {
      const { error } = await supabase?.storage?.from('branding-assets')?.remove([filePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }
};